import React, { useState } from 'react';

import { ApolloError } from '@apollo/client';

import { Button, Text } from '@shared/components/bootstrap';

import { ReasonSelector } from '@admin/components/billing/invoices/forgive/reason_selector';
import { InputFormGroup } from '@admin/components/fields';
import { client } from '@admin/libraries/apollo';
import { useBillingInvoiceForgiveMutation } from '@admin/schema';
import { pollForInvoiceEvent } from '@admin/utils/poll_for_invoice_event';

export const InvoiceForgiveForm: React.FC<{
  invoiceID: string;
  accountID: string;
  onSave(attemptID: string): void;
  onCancel(): void;
}> = ({ invoiceID, accountID, onSave, onCancel }) => {
  const [notes, setNotes] = useState<string | undefined>();
  const [reasonID, setReasonID] = useState<string | undefined>();
  const [error, setError] = useState<string | undefined>();
  const [polling, setPolling] = useState<boolean>(false);

  const [forgive, { loading }] = useBillingInvoiceForgiveMutation({ client });

  const disabled = !notes || !reasonID;

  const onSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    event.stopPropagation();

    if (loading || !notes || !reasonID) {
      return;
    }

    setError(undefined);
    try {
      const response = await forgive({
        variables: {
          invoiceID,
          input: {
            notes,
            reasonID,
          },
        },
      });

      const attemptID = response.data?.result?.attemptID;
      if (!attemptID) {
        setError('Unknown error occurred. Please try again later.');
        return;
      }

      setPolling(true);
      try {
        await pollForInvoiceEvent(accountID, attemptID);
      } finally {
        setPolling(false);
      }
      onSave(attemptID);
    } catch (e: unknown) {
      const message =
        e instanceof ApolloError
          ? e.graphQLErrors.map((err) => err.message).join(' ') || e.message
          : e instanceof Error
            ? e.message
            : 'Request failed. Please try again.';
      setError(message);
    }
  };

  return (
    <form onSubmit={onSubmit}>
      <ReasonSelector reasonID={reasonID} onSelect={setReasonID} />
      <InputFormGroup
        id="forgiveness_notes"
        label="Notes:"
        type="text"
        placeholder="Notes"
        value={notes || ''}
        onChange={(event) => setNotes(event.target.value || undefined)}
      />
      {error && (
        <Text tag="p" style="danger" alignment="center">
          <strong>{error}</strong>
        </Text>
      )}
      <Text alignment="right" tag="div">
        <Button kind="danger" disabled={loading || polling} onClick={onCancel}>
          Cancel
        </Button>{' '}
        <Button kind="primary" type="submit" loading={loading || polling} disabled={disabled}>
          Forgive
        </Button>
      </Text>
    </form>
  );
};
